import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from '@wtag/rcl-card';
import ContentLoaderPlaceholder from '@wtag/rcl-content-loader-placeholder';
import SidePanel from '@wtag/rcl-sidepanel';
import { FormatDateTime, DATE_FORMAT_SHORT_WITH_TIME } from 'sharedWebpack/helpers/dateTime';
import DateTimeLocaleInterpolationWrapper from 'sharedWebpack/DateTimeLocaleInterpolationWrapper';
import AirportChangeSegment from 'sharedWebpack/AirportChangeSegment';
import './styles.scss';

const getAirportData = async (findAirport, code) => {
  const result = await findAirport(code);
  return result?.type === 'AIRPORT_DETAILS_FETCHED' ? result.data : result;
};

const viewAirportDetails = ({ name, placeName, countryName }) =>
  [name, placeName, countryName].filter(Boolean).join(', ');

const StopoverItem = ({ stopover, airports, findAirport, airportChangeMap }) => {
  const stopoverArrivalLocalTime = stopover.arrivalLocaltime || stopover.arrivalLocaltimeInUserTz;

  const [stopoverAirport, setStopoverAirport] = useState(
    (airports && airports[stopover.airportCode]) || null,
  );
  const [arrivalAirport, setArrivalAirport] = useState(null);
  const [departureAirport, setDepartureAirport] = useState(null);

  useEffect(() => {
    setStopoverAirport(null);
    setArrivalAirport(null);
    setDepartureAirport(null);

    const fetchAirports = async () => {
      const fetchedStopoverAirport =
        (airports && airports[stopover.airportCode]) ||
        await getAirportData(findAirport, stopover.airportCode);

      setStopoverAirport(fetchedStopoverAirport);

      const airportChangeData = airportChangeMap.get(fetchedStopoverAirport?.code);
      if (!airportChangeData) return;

      const fetchedArrivalAirport = await getAirportData(findAirport, airportChangeData.arrival);
      setArrivalAirport(fetchedArrivalAirport);

      const fetchedDepartureAirport =
        airportChangeData.departure === fetchedStopoverAirport.code
          ? fetchedStopoverAirport
          : await getAirportData(findAirport, airportChangeData.departure);

      setDepartureAirport(fetchedDepartureAirport);
    };

    fetchAirports();
  }, [stopover.airportCode, findAirport, airportChangeMap, airports]);

  const hasAirportChange = airportChangeMap.has(stopoverAirport?.code);

  return (
    <Card className="duration-stopover__card" key={stopover.airportCode} version="v2">
      {stopoverAirport ? (
        <div className="stopover-details__content">
          <div className="col-grid col-bleed direction-row align-center justify-space-between flight-item-trip-stopover">
            <div className="flight-item-trip-stopover__country">
              <img
                className="flight-item-trip-stopover__country__flag"
                src={stopoverAirport.countryFlag}
                alt={stopoverAirport.countryName}
              />
              {stopover.airportCode}
            </div>
          </div>

          {stopoverAirport.name && (
            <div className="flight-item-trip-stopover__airport-name">
              {viewAirportDetails(stopoverAirport)}
            </div>
          )}

          <div className="flight-item-trip-stopover__arrival-time">
            <DateTimeLocaleInterpolationWrapper
              localeKey="components.flight.stopover.arrival"
              time={FormatDateTime({
                dateTime: stopoverArrivalLocalTime,
                format: DATE_FORMAT_SHORT_WITH_TIME,
              })}
            />
          </div>

          {hasAirportChange &&
            (arrivalAirport && departureAirport ? (
              <AirportChangeSegment
                destinationAirportDetails={arrivalAirport}
                changedAirportDetails={departureAirport}
              />
            ) : (
              <ContentLoaderPlaceholder numberOfLines={2} />
            ))}
        </div>
      ) : (
        <span className="stopover-details__content-placeholder">
          <ContentLoaderPlaceholder numberOfLines={3} />
        </span>
      )}
    </Card>
  );
};

StopoverItem.propTypes = {
  airports: PropTypes.shape({}).isRequired,
  findAirport: PropTypes.func.isRequired,
  stopover: PropTypes.shape({
    airportCode: PropTypes.string.isRequired,
    arrivalLocaltime: PropTypes.string,
    arrivalLocaltimeInUserTz: PropTypes.string,
  }).isRequired,
  airportChangeMap: PropTypes.instanceOf(Map).isRequired,
};

const StopoverSidePanel = ({ stopovers, airports, findAirport, onOpen, onClose, isOpened }) => {
  const [airportChangeMap, setAirportChangeMap] = useState(new Map());

  useEffect(() => {
    const newAirportChangeMap = new Map();
    stopovers
      .filter(stopover => stopover.airportChange)
      .map(stopover => stopover.airportChange)
      .forEach(stopover => {
        newAirportChangeMap.set(stopover.departureAirportCode, {
          arrival: stopover.arrivalAirportCode,
          departure: stopover.departureAirportCode,
        });
      });
    setAirportChangeMap(newAirportChangeMap);
  }, [stopovers]);

  return (
    <SidePanel
      title={I18n.t('components.flight.stopovers.title', { count: stopovers.length })}
      isOpened={isOpened}
      onOpen={onOpen}
      onClose={onClose}
    >
      {stopovers.map(stopover => (
        <StopoverItem
          key={stopover.airportCode}
          stopover={stopover}
          airports={airports}
          findAirport={findAirport}
          airportChangeMap={airportChangeMap}
        />
      ))}
    </SidePanel>
  );
};

StopoverSidePanel.propTypes = {
  airports: PropTypes.shape({}).isRequired,
  findAirport: PropTypes.func.isRequired,
  stopovers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isOpened: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StopoverSidePanel;
